import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, CheckCircle, XCircle, AlertTriangle, FileText,
  MessageSquare, ShieldCheck, User, MapPin, Users, RefreshCw, History, Clock, PauseCircle, Send, TrendingDown
} from 'lucide-react';
import { RegistryLink } from '@/components/RegistryLink';
import AsyncNarrative from '@/components/submission/AsyncNarrative';
import { BindCelebration } from '@/components/BindCelebration';
import { AIRAMark } from '@/components/brand/Marks';
import { PremiumQuoteCard, DimensionBar } from '@/components/submission/ScoringResult';
import { SubmissionBrokerageDial } from '@/components/submission/BrokerageDial';
import BindBlockPanel from '@/components/BindBlockPanel';
import ABRVerificationCard from '@/components/ABRVerificationCard';
import { MarketPositionCard } from '@/components/MarketPositionCard';
import RecordDocuments from '@/components/RecordDocuments';
import EmailDocButton from '@/components/EmailDocButton';
import { completeFirstRunStep } from '@/lib/firstRun';
import ClientRiskNotes from '@/components/ClientRiskNotes';
import { StpBadge } from '@/components/StpBadge';
import QuoteTaxPanel from '@/components/QuoteTaxPanel';
import AuditTrail from '@/components/AuditTrail';
import DecisionPath from '@/components/DecisionPath';
import { ConductFlagsDetail } from '@/components/ConductWatch';
import { BrokerReturnPanel } from '@/components/submission/BrokerReturnPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  scored: { label: 'Scored', color: '#00B4D8' },
  conditions: { label: 'Conditions', color: '#00B4D8' },
  referred: { label: 'Referred', color: '#E8A020' },
  info_requested: { label: 'With Broker', color: '#E8A020' },
  deferred: { label: 'Deferred', color: '#5C6B7A' },
  referred_to_client: { label: 'Referred to Client', color: '#7C5CBF' },
  expired: { label: 'Expired', color: '#64748B' },
  bound: { label: 'Bound', color: '#1D9E75' },
  lost: { label: 'Lost', color: '#B0413E' },
  declined: { label: 'Declined', color: '#E24B4A' },
};

const ACTIVE_QUOTE_STATUSES = ['scored', 'conditions', 'referred', 'info_requested', 'referred_to_client'];

export function ExpiryCountdown({ sub }) {
  if (!sub?.quote_expires_at) return null;
  const isActive = ACTIVE_QUOTE_STATUSES.includes(sub.status);
  const isExpired = sub.status === 'expired';
  if (!isActive && !isExpired) return null;
  const days = Math.ceil((new Date(sub.quote_expires_at) - Date.now()) / 86400000);
  if (isExpired || days < 0) {
    return (
      <span className="px-2 py-0.5 rounded text-[10px] font-semibold inline-flex items-center gap-1"
        style={{ background: 'rgba(226,75,74,0.1)', color: '#E24B4A' }} data-testid="quote-expiry-chip">
        <Clock className="w-3 h-3" />Quote expired {new Date(sub.quote_expires_at).toLocaleDateString('en-AU')}
      </span>
    );
  }
  const color = days <= 2 ? '#E24B4A' : days <= 7 ? '#E8A020' : '#0E7490';
  const bg = days <= 2 ? 'rgba(226,75,74,0.1)' : days <= 7 ? 'rgba(232,160,32,0.12)' : 'rgba(0,180,216,0.1)';
  return (
    <span className="px-2 py-0.5 rounded text-[10px] font-semibold inline-flex items-center gap-1"
      style={{ background: bg, color }} data-testid="quote-expiry-chip">
      <Clock className="w-3 h-3" />{days === 0 ? 'Expires today' : days === 1 ? 'Expires tomorrow' : `Expires in ${days} days`}
    </span>
  );
}

const DECISION_COLORS = { APPROVE: '#1D9E75', CONDITIONS: '#00B4D8', REFER: '#E8A020', DECLINE: '#E24B4A' };

const fmt$ = (v) => v == null ? '—' : `$${Number(v).toLocaleString('en-AU')}`;

function SectionCard({ title, icon: Icon, children, testId, accent }) {
  return (
    <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: accent ? `3px solid ${accent}` : undefined }} data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4" style={{ color: accent || '#1A4C7C' }} />}
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#5C6B7A' }}>{title}</h3>
      </div>
      {children}
    </Card>
  );
}

function Fact({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between py-1 text-xs">
      <span style={{ color: '#5C6B7A' }}>{label}</span>
      <span className={`font-medium ${mono ? 'font-mono' : ''}`} style={{ color: '#0D1B2A' }}>{value ?? '—'}</span>
    </div>
  );
}

const COVER_OPTIONS = [
  { id: 'tools_equipment', label: 'Tools & Equipment' },
  { id: 'management_liability', label: 'Management Liability' },
  { id: 'money_theft', label: 'Money & Theft' },
  { id: 'business_interruption', label: 'Business Interruption' },
];

function RerateCard({ sub, api, onDone }) {
  const sd = sub.submission_data || {};
  const [sumInsured, setSumInsured] = useState(sd.sum_insured || 0);
  const [covers, setCovers] = useState(sd.covers_selected || []);
  const [busy, setBusy] = useState(false);
  const dirty = Number(sumInsured) !== Number(sd.sum_insured || 0)
    || JSON.stringify([...covers].sort()) !== JSON.stringify([...(sd.covers_selected || [])].sort());
  const toggle = (id) => setCovers(c => c.includes(id) ? c.filter(x => x !== id) : [...c, id]);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/submissions/${sub.submission_id}/rerate`, { sum_insured: Number(sumInsured), covers_selected: covers });
      const q = r.data?.scoring_result?.marais_quote;
      toast.success(`Re-rated — gross premium ${q?.gross_premium != null ? fmt$(q.gross_premium) : 'updated'}, decision ${r.data?.decision}`);
      await onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-rate failed');
    }
    setBusy(false);
  };
  return (
    <SectionCard title="Adjust & Re-rate" icon={RefreshCw} testId="quote-rerate-card">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: '#5C6B7A' }}>Sum Insured ($)</label>
          <input type="number" min="1" value={sumInsured} onChange={e => setSumInsured(e.target.value)}
            className="w-full px-3 py-2 text-sm font-mono rounded-md border outline-none focus:border-[#2A6FC4]"
            style={{ borderColor: 'rgba(136,150,168,0.25)', color: '#0D1B2A' }} data-testid="rerate-sum-insured-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: '#5C6B7A' }}>Optional Covers</label>
          <div className="flex flex-wrap gap-1.5">
            {COVER_OPTIONS.map(c => (
              <button key={c.id} onClick={() => toggle(c.id)} data-testid={`rerate-cover-${c.id}`}
                className="px-2.5 py-1.5 rounded-md text-[11px] font-medium border transition-colors"
                style={covers.includes(c.id)
                  ? { borderColor: '#00B4D8', background: 'rgba(0,180,216,0.08)', color: '#0E7490' }
                  : { borderColor: 'rgba(136,150,168,0.25)', color: '#5C6B7A' }}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button onClick={submit} disabled={busy || !dirty || Number(sumInsured) <= 0} data-testid="rerate-submit-btn"
          className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 hover:opacity-90 disabled:opacity-40 transition-opacity"
          style={{ background: '#1A4C7C' }}>
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />{busy ? 'Re-rating...' : 'Re-rate Premium'}
        </button>
        {!dirty && <span className="text-[11px]" style={{ color: '#64748B' }}>Adjust sum insured or covers to re-rate</span>}
        {dirty && <span className="text-[11px]" style={{ color: '#B27516' }}>Re-rating re-scores the risk and disables STP</span>}
      </div>
      {sub.rerated_at && (
        <p className="text-[10px] mt-2" style={{ color: '#64748B' }}>
          Last re-rated {new Date(sub.rerated_at).toLocaleString('en-AU')} by {sub.rerated_by || 'underwriter'}
        </p>
      )}
    </SectionCard>
  );
}

function InfoResponsePanel({ submissionId, api, onDone }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/submissions/${submissionId}/respond-info`, { response: text });
      toast.success('Response sent — quote returned to underwriter review');
      await onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send response');
    }
    setBusy(false);
  };
  return (
    <div className="mt-3 space-y-2" data-testid="info-response-panel">
      <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
        placeholder="Provide the requested information (you can also attach documents below in the Documents section)..."
        className="w-full px-3 py-2 text-xs rounded-md border outline-none focus:border-[#2A6FC4] bg-white"
        style={{ borderColor: 'rgba(136,150,168,0.3)', color: '#0D1B2A' }} data-testid="info-response-input" />
      <button onClick={send} disabled={busy || !text.trim()} data-testid="info-response-submit"
        className="px-4 py-2 rounded-md text-xs font-semibold text-white disabled:opacity-40 hover:opacity-90 transition-opacity"
        style={{ background: '#1A4C7C' }}>
        {busy ? 'Sending...' : 'Send Response to Underwriter'}
      </button>
    </div>
  );
}

function PremiumBandNote({ api, classId, gross }) {
  const [k, setK] = useState(null);
  useEffect(() => {
    api.get('/classes/knowledge').then(r => setK(r.data?.[classId])).catch(() => {});
  }, [api, classId]);
  if (!k || gross == null) return null;
  const b = k.premium_band;
  const within = gross >= b.typical_min && gross <= b.typical_max;
  const fmtB = (v) => v >= 1000 ? `$${(v / 1000).toLocaleString('en-AU')}k` : `$${v}`;
  return (
    <p className="text-xs flex items-center gap-1.5 px-1 -mt-2" data-testid="quote-band-note" style={{ color: within ? '#1D9E75' : '#B27516' }}>
      {within ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
      Premium {within ? 'within' : (gross > b.typical_max ? 'above' : 'below')} the typical {k.name} band ({fmtB(b.typical_min)}–{fmtB(b.typical_max)})
    </p>
  );
}

function RerateHistory({ submissionId, api, refreshKey }) {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get(`/submissions/${submissionId}/audit`)
      .then(r => setEvents((r.data?.events || []).filter(e => e.action === 'rerated')))
      .catch(() => setEvents([]));
  }, [api, submissionId, refreshKey]);
  if (events.length === 0) return null;
  const parse = (e) => Object.fromEntries((e.changes || []).map(c => [c.field, c]));
  return (
    <SectionCard title={`Re-rate History (${events.length})`} icon={History} testId="quote-rerate-history">
      <div className="space-y-0">
        {events.map((e, i) => {
          const c = parse(e);
          const gp = c.gross_premium || {};
          const delta = gp.before != null && gp.after != null ? gp.after - gp.before : null;
          const deltaColor = delta > 0 ? '#E24B4A' : delta < 0 ? '#1D9E75' : '#5C6B7A';
          const oldCovers = c.covers_selected?.before || [];
          const newCovers = c.covers_selected?.after || [];
          const added = newCovers.filter(x => !oldCovers.includes(x));
          const removed = oldCovers.filter(x => !newCovers.includes(x));
          return (
            <div key={e.audit_id} className="relative pl-5 pb-4" data-testid={`rerate-history-entry-${i}`}>
              <span className="absolute left-0 top-1 w-2.5 h-2.5 rounded-full" style={{ background: '#1A4C7C' }} />
              {i < events.length - 1 && <span className="absolute left-[4px] top-4 bottom-0 w-px" style={{ background: 'rgba(136,150,168,0.25)' }} />}
              <div className="flex items-center justify-between flex-wrap gap-1">
                <p className="text-[11px] font-medium" style={{ color: '#0D1B2A' }}>
                  {new Date(e.at).toLocaleString('en-AU')} · {e.user_name}
                </p>
                {delta != null && (
                  <span className="text-xs font-mono font-bold" style={{ color: deltaColor }} data-testid={`rerate-history-delta-${i}`}>
                    {delta >= 0 ? '+' : '−'}${Math.abs(delta).toLocaleString('en-AU')}
                    {gp.before ? ` (${delta >= 0 ? '+' : ''}${((delta / gp.before) * 100).toFixed(1)}%)` : ''}
                  </span>
                )}
              </div>
              <div className="text-[11px] mt-0.5 space-y-0.5" style={{ color: '#5C6B7A' }}>
                {gp.before != null && <p>Gross premium <span className="font-mono">{fmt$(gp.before)}</span> → <span className="font-mono font-semibold" style={{ color: '#0D1B2A' }}>{fmt$(gp.after)}</span></p>}
                {c.sum_insured && c.sum_insured.before !== c.sum_insured.after && (
                  <p>Sum insured <span className="font-mono">{fmt$(c.sum_insured.before)}</span> → <span className="font-mono">{fmt$(c.sum_insured.after)}</span></p>
                )}
                {(added.length > 0 || removed.length > 0) && (
                  <p className="flex flex-wrap gap-1 items-center">Covers:
                    {added.map(x => <span key={x} className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(29,158,117,0.1)', color: '#1D9E75' }}>+ {x.replace(/_/g, ' ')}</span>)}
                    {removed.map(x => <span key={x} className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(226,75,74,0.08)', color: '#E24B4A' }}>− {x.replace(/_/g, ' ')}</span>)}
                  </p>
                )}
                {c.composite_score && c.composite_score.before !== c.composite_score.after && (
                  <p>Score <span className="font-mono">{c.composite_score.before}</span> → <span className="font-mono">{c.composite_score.after}</span>
                    {c.status && c.status.before !== c.status.after ? <> · stage <span className="capitalize">{c.status.before}</span> → <span className="capitalize font-medium" style={{ color: '#0D1B2A' }}>{c.status.after}</span></> : null}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

function ActionBar({ sub, onAction, acting }) {
  const [notesMode, setNotesMode] = useState(null);
  const [notes, setNotes] = useState('');
  const [deferUntil, setDeferUntil] = useState('');
  const [lostReason, setLostReason] = useState('price_too_high');
  const [lostCompetitor, setLostCompetitor] = useState('');
  const [lostWinningPrice, setLostWinningPrice] = useState('');
  const confirmWithNotes = () => { onAction(notesMode, notes, deferUntil, lostReason, lostCompetitor, lostWinningPrice); setNotesMode(null); setNotes(''); setDeferUntil(''); setLostCompetitor(''); setLostWinningPrice(''); };
  const LOST_REASONS = [
    ['price_too_high', 'Price too high'], ['competitor_won', 'Lost to competitor'],
    ['coverage_gaps', "Coverage didn't fit"], ['client_deferred', 'Client not proceeding'],
    ['no_response', 'No response from client'], ['broker_withdrew', 'Broker withdrew'], ['other', 'Other'],
  ];
  const NOTES_PLACEHOLDERS = {
    decline: 'Reason for decline (sent to broker)...',
    refer_broker: 'What information do you need from the broker?',
    defer: 'Why is this quote being deferred? (e.g. awaiting renewal date, market capacity review)',
    refer_to_client: 'Message for the client — terms, subjectivities or conditions to confirm...',
    mark_lost: 'Optional context — competitor name, feedback from the broker...',
  };
  const CONFIRM_LABELS = { decline: 'Decline', refer_broker: 'Info Request', defer: 'Defer', refer_to_client: 'Refer to Client', mark_lost: 'Mark Lost' };
  const CONFIRM_COLORS = { decline: '#E24B4A', refer_broker: '#E8A020', defer: '#5C6B7A', refer_to_client: '#7C5CBF', mark_lost: '#B0413E' };
  return (
    <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="quote-action-bar">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#5C6B7A' }}>Underwriter Actions</h3>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => onAction('approve')} disabled={!!acting} data-testid="quote-approve-btn"
          className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 transition-opacity"
          style={{ background: '#1D9E75' }}>
          <CheckCircle className="w-3.5 h-3.5" />{acting === 'approve' ? 'Binding...' : 'Approve & Bind'}
        </button>
        <button onClick={() => onAction('conditions')} disabled={!!acting} data-testid="quote-conditions-btn"
          className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 transition-opacity"
          style={{ background: '#00B4D8' }}>
          <ShieldCheck className="w-3.5 h-3.5" />Approve with Conditions
        </button>
        <button onClick={() => setNotesMode(notesMode === 'refer_broker' ? null : 'refer_broker')} disabled={!!acting} data-testid="quote-refer-btn"
          className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 transition-opacity"
          style={{ background: '#E8A020' }}>
          <MessageSquare className="w-3.5 h-3.5" />Request Info
        </button>
        <button onClick={() => setNotesMode(notesMode === 'refer_to_client' ? null : 'refer_to_client')} disabled={!!acting} data-testid="quote-refer-client-btn"
          className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 transition-opacity"
          style={{ background: '#7C5CBF' }}>
          <Send className="w-3.5 h-3.5" />Refer to Client
        </button>
        <button onClick={() => setNotesMode(notesMode === 'defer' ? null : 'defer')} disabled={!!acting} data-testid="quote-defer-btn"
          className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 transition-opacity"
          style={{ background: '#5C6B7A' }}>
          <PauseCircle className="w-3.5 h-3.5" />Defer
        </button>
        <button onClick={() => setNotesMode(notesMode === 'mark_lost' ? null : 'mark_lost')} disabled={!!acting} data-testid="quote-mark-lost-btn"
          className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 transition-opacity"
          style={{ background: '#B0413E' }}>
          <TrendingDown className="w-3.5 h-3.5" />Mark Lost
        </button>
        <button onClick={() => setNotesMode(notesMode === 'decline' ? null : 'decline')} disabled={!!acting} data-testid="quote-decline-btn"
          className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 transition-opacity"
          style={{ background: '#E24B4A' }}>
          <XCircle className="w-3.5 h-3.5" />Decline
        </button>
      </div>
      {notesMode && (
        <div className="mt-3 space-y-2" data-testid="quote-action-notes">
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder={NOTES_PLACEHOLDERS[notesMode]}
            className="w-full px-3 py-2 text-xs rounded-md border outline-none focus:border-[#2A6FC4]"
            style={{ borderColor: 'rgba(136,150,168,0.25)', color: '#0D1B2A' }} data-testid="quote-notes-input" />
          {notesMode === 'defer' && (
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Revisit on</label>
              <input type="date" value={deferUntil} onChange={e => setDeferUntil(e.target.value)}
                className="px-3 py-1.5 text-xs rounded-md border outline-none focus:border-[#2A6FC4]"
                style={{ borderColor: 'rgba(136,150,168,0.25)', color: '#0D1B2A' }} data-testid="quote-defer-until-input" />
              <span className="text-[10px]" style={{ color: '#64748B' }}>(optional)</span>
            </div>
          )}
          {notesMode === 'mark_lost' && (
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Reason</label>
              <select value={lostReason} onChange={e => setLostReason(e.target.value)} data-testid="quote-lost-reason-select"
                className="px-3 py-1.5 text-xs rounded-md border outline-none focus:border-[#2A6FC4]"
                style={{ borderColor: 'rgba(136,150,168,0.25)', color: '#0D1B2A' }}>
                {LOST_REASONS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
              <input type="text" value={lostCompetitor} onChange={e => setLostCompetitor(e.target.value)}
                placeholder="Competitor (if known)" data-testid="quote-lost-competitor-input"
                className="px-3 py-1.5 text-xs rounded-md border outline-none focus:border-[#2A6FC4] w-40"
                style={{ borderColor: 'rgba(136,150,168,0.25)', color: '#0D1B2A' }} />
              <input type="number" min="0" step="1" value={lostWinningPrice} onChange={e => setLostWinningPrice(e.target.value)}
                placeholder="Winning price $ (if known)" data-testid="quote-lost-winning-price-input"
                className="px-3 py-1.5 text-xs rounded-md border outline-none focus:border-[#2A6FC4] w-44"
                style={{ borderColor: 'rgba(136,150,168,0.25)', color: '#0D1B2A' }} />
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={confirmWithNotes} disabled={notesMode !== 'decline' && notesMode !== 'mark_lost' && !notes.trim()} data-testid="quote-notes-confirm"
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white disabled:opacity-40"
              style={{ background: CONFIRM_COLORS[notesMode] }}>
              Confirm {CONFIRM_LABELS[notesMode]}
            </button>
            <button onClick={() => setNotesMode(null)} className="px-3 py-1.5 rounded-md text-xs font-medium" style={{ color: '#5C6B7A' }}>Cancel</button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function QuoteDetailsPage() {
  const { submissionId } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role !== 'broker';
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(null);
  const [celebrate, setCelebrate] = useState(null);

  const load = useCallback(() => {
    return api.get(`/submissions/${submissionId}`)
      .then(r => {
        setSub(r.data);
        if (r.data?.scoring_result) completeFirstRunStep(api, 'review');
      })
      .catch(err => setError(err?.response?.status === 404 ? 'Submission not found' : (err?.response?.data?.detail || 'Failed to load submission')))
      .finally(() => setLoading(false));
  }, [api, submissionId]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (decision, notes = '', deferUntil = '', lostReason = '', lostCompetitor = '', lostWinningPrice = '') => {
    setActing(decision);
    const t0 = Date.now();
    try {
      let extra = decision === 'defer' && deferUntil ? `&defer_until=${encodeURIComponent(deferUntil)}` : '';
      if (decision === 'mark_lost') {
        extra = `&lost_reason=${encodeURIComponent(lostReason)}`;
        if (lostCompetitor.trim()) extra += `&competitor=${encodeURIComponent(lostCompetitor.trim())}`;
        if (lostWinningPrice && Number(lostWinningPrice) > 0) extra += `&winning_price=${encodeURIComponent(lostWinningPrice)}`;
      }
      await api.post(`/submissions/${submissionId}/decide?decision=${decision}&notes=${encodeURIComponent(notes)}${extra}`);
      toast.success({ approve: 'Quote approved — policy bound', conditions: 'Approved with conditions', refer_broker: 'Information requested from broker', decline: 'Submission declined', defer: 'Quote deferred', refer_to_client: 'Referred to client — broker notified', mark_lost: 'Marked as lost — reason recorded for analytics' }[decision]);
      await load();
      if (decision === 'approve') {
        const fresh = await api.get(`/submissions/${submissionId}`);
        if (fresh.data.policy_id) {
          completeFirstRunStep(api, 'bind');
          setCelebrate({ policyId: fresh.data.policy_id, policyNumber: fresh.data.policy_number, seconds: ((Date.now() - t0) / 1000).toFixed(1) });
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
    setActing(null);
  };

  const sr = sub?.scoring_result || {};
  const sd = sub?.submission_data || {};
  const sortedDimensions = useMemo(() =>
    Object.entries(sub?.scoring_result?.dimension_scores || {}).sort((a, b) => b[1].score - a[1].score), [sub]);

  if (loading) return <div className="space-y-3" data-testid="quote-details-loading">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;
  if (error) return (
    <div className="py-16 text-center" data-testid="quote-details-error">
      <p className="text-sm font-medium" style={{ color: '#E24B4A' }}>{error}</p>
      <button onClick={() => navigate('/new-business')} className="mt-3 text-xs underline" style={{ color: '#1A4C7C' }}>Back to New Business</button>
    </div>
  );

  const status = STATUS_META[sub.status] || { label: sub.status, color: '#5C6B7A' };
  const withBroker = sub.status === 'info_requested' && !!sub.broker_action;
  const dc = DECISION_COLORS[sub.decision] || '#5C6B7A';
  const showActions = isStaff && !['bound', 'lost'].includes(sub.status);

  return (
    <div className="space-y-4" data-testid="quote-details-page">
      {celebrate && (
        <BindCelebration policyId={celebrate.policyId} policyNumber={celebrate.policyNumber} seconds={celebrate.seconds} onClose={() => setCelebrate(null)} />
      )}
      <BindBlockPanel api={api} submissionId={submissionId} isStaff={isStaff} onResolved={load} />
      {isStaff && (() => {
        const dbp = (sub.scoring_result?.override_rules_triggered || []).find(r => r.rule === 'dbp_limit');
        return dbp ? (
          <div className="flex items-start gap-2.5 px-4 py-3 rounded-md" data-testid="dbp-limit-warning"
            style={{ background: 'rgba(232,160,32,0.09)', border: '1px solid rgba(232,160,32,0.35)' }}>
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#E8A020' }} />
            <div>
              <p className="text-xs font-semibold" style={{ color: '#B27516' }}>DBP limit adequacy — NSW statutory duty of care (s37, non-apportionable after Pafburn)</p>
              <p className="text-[11px] mt-0.5" style={{ color: '#3A4A5A' }}>{dbp.desc} A practitioner can face 100% of a defect claim regardless of their share of the work — confirm the limit with the insured before binding.</p>
            </div>
          </div>
        ) : null;
      })()}
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate('/new-business')} data-testid="quote-back-btn"
            className="mt-1 p-1.5 rounded-md hover:bg-white transition-colors" style={{ color: '#5C6B7A' }} title="Back to pipeline">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold" style={{ color: '#0D1B2A' }} data-testid="quote-business-name">{sub.business_name}</h1>
              <span className="px-2 py-0.5 rounded text-[10px] font-semibold capitalize" style={{ background: status.color + '15', color: status.color }} data-testid="quote-status-badge">{status.label}</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: dc + '15', color: dc }} data-testid="quote-decision-badge">{withBroker ? `${sub.decision} · awaiting broker` : sub.decision}</span>
              {sub.insurance_class_name && <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(26,76,124,0.1)', color: '#1A4C7C' }} data-testid="quote-class-badge">{sub.insurance_class_name}</span>}
              {sub.stp && ['bound', 'declined'].includes(sub.status) && !sub.referred_by_broker && <StpBadge />}
              <ExpiryCountdown sub={sub} />
            </div>
            <p className="text-xs mt-1 font-mono" style={{ color: '#5C6B7A' }}>
              <RegistryLink abn={sub.abn} nzbn={sub.nzbn} prefix />
              <span className="mx-2">·</span>Submitted {new Date(sub.created_at).toLocaleDateString('en-AU')} by {sub.broker_name}
              <span className="mx-2">·</span>Ref {sub.client_ref || sub.submission_id?.slice(0, 8)}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Composite Score</p>
          <p className="text-2xl font-bold font-mono" style={{ color: dc }} data-testid="quote-score">{sub.composite_score}<span className="text-xs font-normal" style={{ color: '#5C6B7A' }}>/10K</span></p>
          {sr.marais_quote && (
            <a href={`${BACKEND_URL}/api/documents/quote-summary/${sub.submission_id}`} target="_blank" rel="noopener noreferrer"
              data-testid="quote-client-pdf-btn" onClick={() => completeFirstRunStep(api, 'deliver')}
              className="mt-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors hover:bg-white"
              style={{ border: '1px solid rgba(26,76,124,0.3)', color: '#1A4C7C' }}>
              <FileText className="w-3 h-3" />Client Quote PDF
            </a>
          )}
          {sr.marais_quote && (
            <a href={`${BACKEND_URL}/api/documents/broker-quote/${sub.submission_id}`} target="_blank" rel="noopener noreferrer"
              data-testid="quote-broker-pdf-btn"
              className="mt-1 ml-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors hover:bg-white"
              style={{ border: '1px solid rgba(232,160,32,0.4)', color: '#A8720E' }}>
              <FileText className="w-3 h-3" />Broker Quote PDF
            </a>
          )}
          {sr.marais_quote && (
            <a href={`${BACKEND_URL}/api/documents/cover-note/${sub.submission_id}`} target="_blank" rel="noopener noreferrer"
              data-testid="cover-note-pdf-btn"
              className="mt-1 ml-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors hover:bg-white"
              style={{ border: '1px solid rgba(29,158,117,0.4)', color: '#1D9E75' }} title="Cover Note — interim contract of insurance under s38 ICA (PDF)">
              <FileText className="w-3 h-3" />Cover Note (s38)
            </a>
          )}
          {sub && (sub.insurance_class || sub.submission_data?.insurance_class) && (
            <a href={`${BACKEND_URL}/api/documents/proposal-form/${sub.insurance_class || sub.submission_data?.insurance_class}/completed/${sub.submission_id}`} target="_blank" rel="noopener noreferrer"
              data-testid="completed-proposal-pdf-btn"
              className="mt-1 ml-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors hover:bg-white"
              style={{ border: '1px solid rgba(0,180,216,0.4)', color: '#0E7490' }} title="Completed, signable proposal form — answers merged from this submission (PDF)">
              <FileText className="w-3 h-3" />Completed Proposal
            </a>
          )}
          {sub && (
            <a href={`${BACKEND_URL}/api/documents/statement-of-facts/${sub.submission_id}`} target="_blank" rel="noopener noreferrer"
              data-testid="sof-pdf-btn"
              className="mt-1 ml-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors hover:bg-white"
              style={{ border: '1px solid rgba(92,107,122,0.4)', color: '#5C6B7A' }} title="Statement of facts — declared risk information (PDF)">
              <FileText className="w-3 h-3" />Statement of Facts
            </a>
          )}
          {(sr.decision || '').includes('REFER') && (
            <a href={`${BACKEND_URL}/api/documents/terms-memo/${sub.submission_id}`} target="_blank" rel="noopener noreferrer"
              data-testid="terms-memo-pdf-btn"
              className="mt-1 ml-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors hover:bg-white"
              style={{ border: '1px solid rgba(124,92,191,0.4)', color: '#7C5CBF' }} title="Terms memorandum for the referral (PDF)">
              <FileText className="w-3 h-3" />Terms Memo
            </a>
          )}
          {(sr.decision || '').includes('DECLINE') && (
            <a href={`${BACKEND_URL}/api/documents/decline-letter/${sub.submission_id}`} target="_blank" rel="noopener noreferrer"
              data-testid="decline-letter-pdf-btn"
              className="mt-1 ml-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors hover:bg-white"
              style={{ border: '1px solid rgba(226,75,74,0.4)', color: '#B03230' }} title="Decline letter (PDF)">
              <FileText className="w-3 h-3" />Decline Letter
            </a>
          )}
          {sr.marais_quote && (
            <span className="ml-1.5 inline-block align-middle">
              <EmailDocButton docType="quote" refId={sub.submission_id} docLabel="Insurance Quotation"
                className="!px-2.5 !py-1 !text-[10px]" />
            </span>
          )}
          <a href={`${BACKEND_URL}/api/documents/schedule-of-answers/${sub.submission_id}`} target="_blank" rel="noopener noreferrer"
            data-testid="quote-schedule-answers-btn"
            className="mt-1 ml-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors hover:bg-white"
            style={{ border: '1px solid rgba(124,92,191,0.4)', color: '#7C5CBF' }}>
            <FileText className="w-3 h-3" />Schedule of Answers
          </a>
        </div>
      </div>

      {isStaff && <ABRVerificationCard api={api} submissionId={submissionId} />}

      {/* Bound policy banner */}
      {sub.policy_id && (
        <Card className="rounded-md p-4 flex items-center justify-between flex-wrap gap-3" style={{ background: 'rgba(29,158,117,0.06)', border: '1px solid rgba(29,158,117,0.3)' }} data-testid="quote-policy-banner">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" style={{ color: '#1D9E75' }} />
            <span className="text-sm font-semibold" style={{ color: '#1D9E75' }}>Policy bound</span>
            <span className="text-xs font-mono" style={{ color: '#0D1B2A' }} data-testid="quote-bound-policy-number">{sub.policy_number || sub.policy_id}</span>
          </div>
          <div className="flex gap-2">
            <a href={`${BACKEND_URL}/api/documents/policy-schedule/${sub.policy_id}`} target="_blank" rel="noopener noreferrer" data-testid="quote-download-schedule"
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white flex items-center gap-1.5" style={{ background: '#1A4C7C' }}>
              <FileText className="w-3 h-3" />Policy Schedule
            </a>
            <a href={`${BACKEND_URL}/api/documents/certificate/${sub.policy_id}`} target="_blank" rel="noopener noreferrer" data-testid="quote-download-certificate"
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white flex items-center gap-1.5" style={{ background: '#1D9E75' }}>
              <FileText className="w-3 h-3" />Certificate of Currency
            </a>
            <EmailDocButton docType="closing_pack" refId={sub.policy_id} docLabel="Broker Closing Pack" />
          </div>
        </Card>
      )}

      {sub.status === 'declined' && (
        <Card className="rounded-md p-4 flex items-center justify-between flex-wrap gap-3" style={{ background: 'rgba(226,75,74,0.05)', border: '1px solid rgba(226,75,74,0.25)' }} data-testid="quote-declined-banner">
          <div className="flex items-center gap-2 text-xs" style={{ color: '#E24B4A' }}>
            <XCircle className="w-4 h-4" /><span className="font-semibold">Declined</span>
            {(sub.manual_decision_notes || sr.decision_reason) && <span style={{ color: '#0D1B2A' }}>— {sub.manual_decision_notes || sr.decision_reason}</span>}
          </div>
          <a href={`${BACKEND_URL}/api/documents/declinature/${sub.submission_id}`} target="_blank" rel="noopener noreferrer" data-testid="quote-download-declinature"
            className="px-3 py-1.5 rounded-md text-xs font-semibold text-white flex items-center gap-1.5" style={{ background: '#E24B4A' }}>
            <FileText className="w-3 h-3" />Declinature Letter
          </a>
        </Card>
      )}

      {sub.status === 'deferred' && (
        <Card className="rounded-md p-4" style={{ background: 'rgba(92,107,122,0.06)', border: '1px solid rgba(92,107,122,0.3)' }} data-testid="quote-deferred-banner">
          <div className="flex items-center gap-2 text-xs" style={{ color: '#5C6B7A' }}>
            <PauseCircle className="w-4 h-4" /><span className="font-semibold">Deferred</span>
            {sub.deferred_by && <span>by {sub.deferred_by}</span>}
            {sub.defer_until && <span className="font-medium" style={{ color: '#0D1B2A' }}>· revisit on {new Date(sub.defer_until).toLocaleDateString('en-AU')}</span>}
          </div>
          {sub.defer_reason && <p className="text-xs mt-1" style={{ color: '#0D1B2A' }}>{sub.defer_reason}</p>}
        </Card>
      )}

      {sub.status === 'referred_to_client' && (
        <Card className="rounded-md p-4" style={{ background: 'rgba(124,92,191,0.06)', border: '1px solid rgba(124,92,191,0.3)' }} data-testid="quote-referred-client-banner">
          <div className="flex items-center gap-2 text-xs" style={{ color: '#7C5CBF' }}>
            <Send className="w-4 h-4" /><span className="font-semibold">Referred to client</span>
            {sub.referred_to_client_by && <span>by {sub.referred_to_client_by}</span>}
            <span style={{ color: '#5C6B7A' }}>— broker notified by email to take terms to the client</span>
          </div>
          {sub.client_referral_note && <p className="text-xs mt-1" style={{ color: '#0D1B2A' }}>{sub.client_referral_note}</p>}
        </Card>
      )}

      {sub.status === 'lost' && (
        <Card className="rounded-md p-4" style={{ background: 'rgba(176,65,62,0.06)', border: '1px solid rgba(176,65,62,0.3)' }} data-testid="quote-lost-banner">
          <div className="flex items-center gap-2 text-xs" style={{ color: '#B0413E' }}>
            <TrendingDown className="w-4 h-4" /><span className="font-semibold">Business lost</span>
            {sub.lost_by && <span>marked by {sub.lost_by}</span>}
            {sub.lost_reason && <span className="px-2 py-0.5 rounded font-semibold" style={{ background: 'rgba(176,65,62,0.12)' }}>{sub.lost_reason.replace(/_/g, ' ')}</span>}
            {sub.lost_competitor && <span className="px-2 py-0.5 rounded" style={{ background: 'rgba(176,65,62,0.12)' }} data-testid="quote-lost-competitor-chip">to {sub.lost_competitor}</span>}
            {sub.lost_winning_price > 0 && <span className="px-2 py-0.5 rounded font-mono" style={{ background: 'rgba(176,65,62,0.12)' }} data-testid="quote-lost-price-chip">won at ${Number(sub.lost_winning_price).toLocaleString()}</span>}
          </div>
          {sub.lost_notes && <p className="text-xs mt-1" style={{ color: '#0D1B2A' }}>{sub.lost_notes}</p>}
        </Card>
      )}

      {sub.status === 'expired' && (
        <Card className="rounded-md p-4 flex items-center gap-2" style={{ background: 'rgba(136,150,168,0.08)', border: '1px solid rgba(136,150,168,0.35)' }} data-testid="quote-expired-banner">
          <Clock className="w-4 h-4" style={{ color: '#64748B' }} />
          <p className="text-xs" style={{ color: '#5C6B7A' }}>
            <b style={{ color: '#0D1B2A' }}>This quote has expired</b>
            {sub.quote_expires_at && <> on {new Date(sub.quote_expires_at).toLocaleDateString('en-AU')}</>} — re-rate the submission to refresh terms before binding.
          </p>
        </Card>
      )}

      {sub.status === 'info_requested' && sub.broker_action && (
        <BrokerReturnPanel sub={sub} api={api} onUpdated={load} isStaff={isStaff} showOpen={false} />
      )}

      {sub.status === 'declined' && sub.broker_may_refer && (
        <BrokerReturnPanel sub={sub} api={api} onUpdated={load} isStaff={isStaff} showOpen={false} />
      )}

      {sub.referred_by_broker && sub.status === 'referred' && (
        <Card className="rounded-md p-4" style={{ background: 'rgba(232,160,32,0.06)', border: '1px solid rgba(232,160,32,0.3)' }} data-testid="quote-broker-referred-banner">
          <p className="text-xs" style={{ color: '#B27516' }}>
            <b>Referred by the broker</b> ({sub.referred_by_broker.by}{sub.referred_by_broker.at ? `, ${new Date(sub.referred_by_broker.at).toLocaleDateString('en-AU')}` : ''}): <span style={{ color: '#0D1B2A' }}>{sub.referred_by_broker.note}</span>
          </p>
        </Card>
      )}

      {sub.status === 'info_requested' && !sub.broker_action && sub.info_requested && (
        <Card className="rounded-md p-4" style={{ background: 'rgba(232,160,32,0.06)', border: '1px solid rgba(232,160,32,0.3)' }} data-testid="quote-info-requested-banner">
          <p className="text-xs" style={{ color: '#B27516' }}>
            <b>Information requested</b> by {sub.info_requested_by || 'underwriter'}: {typeof sub.info_requested === 'string' ? sub.info_requested : (sub.info_requested.fields || []).join(', ')}
          </p>
          {!isStaff && <InfoResponsePanel submissionId={sub.submission_id} api={api} onDone={load} />}
        </Card>
      )}

      {sub.info_response && sub.status !== 'info_requested' && (
        <Card className="rounded-md p-4" style={{ background: 'rgba(29,158,117,0.05)', border: '1px solid rgba(29,158,117,0.25)' }} data-testid="quote-info-response-banner">
          <p className="text-xs" style={{ color: '#1D9E75' }}>
            <b>Broker response</b> from {sub.info_response_by || 'broker'}{sub.info_response_at ? ` (${new Date(sub.info_response_at).toLocaleDateString('en-AU')})` : ''}: <span style={{ color: '#0D1B2A' }}>{sub.info_response}</span>
          </p>
        </Card>
      )}

      {showActions && <ActionBar sub={sub} onAction={handleAction} acting={acting} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: risk & rating */}
        <div className="lg:col-span-2 space-y-4">
          <SectionCard title="Risk & Rating Breakdown" icon={ShieldCheck} testId="quote-rating-card">
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div title={sr.confidence_detail ? `${sr.confidence_detail.method}` : undefined}><p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Confidence</p><p className="text-lg font-bold" style={{ color: '#0D1B2A' }}>{sr.confidence != null ? `${(sr.confidence * 100).toFixed(0)}%` : '—'}</p>{sr.confidence_detail && <p className="text-[9px] capitalize" style={{ color: '#5C6B7A' }}>{sr.confidence_detail.label} certainty</p>}</div>
              <div><p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Data Completeness</p><p className="text-lg font-bold" style={{ color: '#0D1B2A' }}>{sr.data_completeness_pct != null ? `${sr.data_completeness_pct}%` : '—'}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Score Version</p><p className="text-lg font-bold font-mono" style={{ color: '#0D1B2A' }}>{sr.score_version || '—'}</p></div>
            </div>
            {sr.decision_reason && <p className="text-xs p-2.5 rounded-md mb-3" style={{ background: '#F5F7FA', color: '#0D1B2A' }}>{sr.decision_reason}</p>}
            {sortedDimensions.length > 0 && (
              <div className="space-y-2">{sortedDimensions.map(([dim, data]) => <DimensionBar key={dim} dim={dim} data={data} />)}</div>
            )}
            {sr.override_rules_triggered?.length > 0 && (
              <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#E8A020' }}>Override Rules Triggered</p>
                {sr.override_rules_triggered.map((r, i) => (
                  <div key={i} className="py-0.5" data-testid={`quote-override-${r.rule || i}`}>
                    <p className="text-xs flex items-center gap-2" style={{ color: '#0D1B2A' }}>
                      <AlertTriangle className="w-3 h-3 shrink-0" style={{ color: r.cleared ? '#1D9E75' : '#E8A020' }} />
                      <span className="font-mono text-[10px] px-1 rounded" style={{ background: 'rgba(232,160,32,0.1)', color: '#B27516' }}>{r.rule}</span>
                      <span style={{ textDecoration: r.cleared ? 'line-through' : 'none' }}>{r.desc} — <b>{r.action}</b></span>
                    </p>
                    {r.citation && <p className="ml-5 font-mono text-[10px] break-words" style={{ color: '#5C6B7A' }} data-testid={`quote-citation-${r.rule}`}>{r.citation}</p>}
                    {r.cleared && <p className="ml-5 text-[10px]" style={{ color: '#1D9E75' }} data-testid={`quote-cleared-${r.rule}`}>Cleared by {r.cleared.by_name} · {new Date(r.cleared.at).toLocaleDateString('en-AU')} — {r.cleared.note}</p>}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {showActions && <RerateCard sub={sub} api={api} onDone={load} />}

          <DecisionPath trace={sub.decision_trace} modelVersion={sub.model_version}
            serviceVersion={sub.service_version} />

          {sr.marais_quote && <PremiumQuoteCard q={sr.marais_quote} />}
          {sr.marais_quote?.gross_premium != null && !sub.policy_id && !['bound', 'lost'].includes(sub.status) && (
            <SubmissionBrokerageDial api={api} submissionId={sub.submission_id} quote={sr.marais_quote} onChange={load} />
          )}
          {sr.market_position && <MarketPositionCard mp={sr.market_position} />}
          {sr.marais_quote?.gross_premium != null && (
            <PremiumBandNote api={api} classId={sub.insurance_class || 'sme_commercial'} gross={sr.marais_quote.gross_premium} />
          )}
          {sr.marais_quote?.gross_premium != null && (
            <QuoteTaxPanel api={api} submissionId={sub.submission_id} apiBase={`${BACKEND_URL}/api`} />
          )}

          <RerateHistory submissionId={sub.submission_id} api={api} refreshKey={sub.rerated_at} />

          <AsyncNarrative sr={sr} submissionId={sub.submission_id} />

          <SectionCard title="Documents" icon={FileText} testId="quote-documents-card">
            <RecordDocuments recordType="submission" recordId={sub.submission_id} />
          </SectionCard>

          <SectionCard title="Status Timeline" icon={FileText} testId="quote-timeline-card">
            <AuditTrail submissionId={sub.submission_id} />
          </SectionCard>
        </div>

        {/* Right: applicant & context */}
        <div className="space-y-4">
          <SectionCard title="Applicant Snapshot" icon={Building2} testId="quote-applicant-card">
            <Fact label="ABN / NZBN" value={<RegistryLink abn={sub.abn} nzbn={sub.nzbn} style={{ color: '#0D1B2A' }} />} mono />
            {sub.acn && <Fact label="ACN" value={sub.acn} mono />}
            <Fact label="Class" value={sub.insurance_class_name || sd.class || 'SME Commercial'} />
            <Fact label="Annual Turnover" value={fmt$(sd.annual_turnover)} mono />
            <Fact label="Sum Insured" value={fmt$(sd.sum_insured)} mono />
            <Fact label="Employees" value={sd.employee_count} mono />
            <Fact label="Years Trading" value={sd.years_trading} mono />
            <Fact label="Claims (5yr)" value={sd.claims_count} mono />
            {sd.industry_code && <Fact label="Industry" value={sd.industry_code} />}
            {sub.entity_id && isStaff && (
              <button onClick={() => navigate(`/client/${sub.entity_id}`)} data-testid="quote-client-file-btn"
                className="mt-2 w-full py-1.5 rounded-md text-xs font-semibold transition-colors hover:opacity-80"
                style={{ background: 'rgba(26,76,124,0.08)', color: '#1A4C7C' }}>
                Open Client File
              </button>
            )}
          </SectionCard>

          <SectionCard title="Broker" icon={User} testId="quote-broker-card">
            <Fact label="Name" value={sub.broker_name} />
            <Fact label="Email" value={sub.broker_email} />
          </SectionCard>

          {sub.location_risk && (
            <SectionCard title="Location Risk" icon={MapPin} testId="quote-location-card">
              {['flood_zone', 'fire_zone', 'cyclone_zone', 'surge_zone', 'earthquake_zone'].filter(k => sub.location_risk[k]).map(k => (
                <Fact key={k} label={k === 'surge_zone' ? 'Storm Tide' : k.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())} value={String(sub.location_risk[k]).replace(/_/g, ' ').toUpperCase()} />
              ))}
              {sub.location_risk.crime_index != null && <Fact label="Crime Index" value={sub.location_risk.crime_index} mono />}
              {['flood_zone', 'fire_zone', 'surge_zone'].filter(k => sub.location_risk.hazard_evidence?.[k]?.determination).map(k => (
                <p key={k} className="text-[10px] mt-1.5 leading-snug" style={{ color: '#5C6B7A' }} data-testid={`quote-determination-${k}`}>
                  <span className="font-semibold uppercase mr-1" style={{ color: '#1D9E75' }}>Measured</span>
                  {sub.location_risk.hazard_evidence[k].determination}
                  {k === 'surge_zone' ? ' · prices, never refers' : sub.location_risk.hazard_evidence[k].tier && ` · ${sub.location_risk.hazard_evidence[k].tier} layer decides`}
                </p>
              ))}
            </SectionCard>
          )}

          {sub.directors?.length > 0 && (
            <SectionCard title="Directors" icon={Users} testId="quote-directors-card">
              {sub.directors.map((d, i) => <p key={i} className="text-xs py-0.5" style={{ color: '#0D1B2A' }}>{typeof d === 'string' ? d : d.name}</p>)}
            </SectionCard>
          )}

          {sub.corporate_group && (
            <SectionCard title="Corporate Group" icon={Building2} accent="#1A4C7C" testId="quote-corp-group-card">
              <p className="text-sm font-bold mb-1" style={{ color: '#0D1B2A' }}>{sub.corporate_group.group_name}</p>
              <Fact label="Entities" value={sub.corporate_group.entity_count?.toLocaleString()} mono />
              <Fact label="Total Employees" value={sub.corporate_group.total_employees?.toLocaleString()} mono />
              <Fact label="Locations" value={sub.corporate_group.city_count} mono />
            </SectionCard>
          )}

          {sub.duplicate_warning?.count > 0 && (
            <SectionCard title={`Duplicates (${sub.duplicate_warning.count})`} icon={AlertTriangle} accent="#E8A020" testId="quote-duplicates-card">
              {sub.duplicate_warning.recent?.map(d => (
                <p key={d.submission_id} className="text-xs py-0.5" style={{ color: '#5C6B7A' }}>
                  <span className="font-mono">{d.submission_id?.slice(0, 8)}</span> · {d.decision || d.status} · {d.created_at ? new Date(d.created_at).toLocaleDateString('en-AU') : ''}
                </p>
              ))}
            </SectionCard>
          )}

          {isStaff && sub.conduct_flags?.length > 0 && (
            <SectionCard title="Conduct Flags" icon={AlertTriangle} accent="#E24B4A" testId="quote-conduct-card">
              <ConductFlagsDetail flags={sub.conduct_flags} />
            </SectionCard>
          )}

          {sub.entity_id && (
            <SectionCard title="Client Risk Notes" icon={MessageSquare} testId="quote-risk-notes-card">
              <ClientRiskNotes entityId={sub.entity_id} editable={isStaff} compact />
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}
